import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, TrendingUp, Users, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { featuredProjects } from '../data/mock';

const Projects = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState(featuredProjects);

  useEffect(() => {
    if (selectedTab === 'all') {
      setFilteredProjects(featuredProjects);
    } else {
      setFilteredProjects(featuredProjects.filter(p => p.status === selectedTab));
    }
  }, [selectedTab]);

  const getTimeRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h`;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'live': return 'bg-brand-primary text-black';
      case 'upcoming': return 'bg-blue-500 text-white';
      case 'completed': return 'bg-white/20 text-white';
      default: return 'bg-white/20 text-white';
    }
  };

  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="display-large text-white mb-4">Token Launch Projects</h1>
          <p className="body-large text-white/70 max-w-2xl mx-auto">
            Discover and invest in carefully vetted blockchain projects with massive growth potential
          </p>
        </div>

        {/* Filter Tabs */}
        <Tabs defaultValue="all" className="mb-12" onValueChange={setSelectedTab}>
          <TabsList className="bg-white/5 border border-white/25 p-1 w-full md:w-auto mx-auto flex justify-center">
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-black text-white px-8"
            >
              All Projects
            </TabsTrigger>
            <TabsTrigger 
              value="live"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-black text-white px-8"
            >
              Live
            </TabsTrigger>
            <TabsTrigger 
              value="upcoming"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-black text-white px-8"
            >
              Upcoming
            </TabsTrigger>
            <TabsTrigger 
              value="completed"
              className="data-[state=active]:bg-brand-primary data-[state=active]:text-black text-white px-8"
            >
              Completed
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <Card 
              key={project.id} 
              className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300 group"
            >
              <CardContent className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <img 
                    src={project.logo} 
                    alt={project.name} 
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-sm ${getStatusColor(project.status)}`}>
                      {project.status.toUpperCase()}
                    </span>
                    <span className="px-3 py-1 bg-white/10 text-white text-xs font-medium rounded-sm">
                      {project.network}
                    </span>
                  </div>
                </div>

                {/* Project Info */}
                <h3 className="heading-2 text-white mb-2">{project.name}</h3>
                <p className="text-white/60 text-sm mb-1">{project.symbol}</p>
                <p className="text-white/70 text-sm mb-4 leading-relaxed">{project.description}</p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-white/5 rounded-sm">
                  <div>
                    <div className="text-white/60 text-xs mb-1">Token Price</div>
                    <div className="text-white font-semibold">${project.tokenPrice}</div>
                  </div>
                  <div>
                    <div className="text-white/60 text-xs mb-1">Participants</div>
                    <div className="text-white font-semibold">{project.participants.toLocaleString()}</div>
                  </div>
                </div>

                {/* Progress */}
                {project.status !== 'upcoming' && (
                  <>
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Progress</span>
                        <span className="text-brand-primary font-medium">{project.progress.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-brand-primary transition-all duration-500"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/60">Raised: ${project.raised}</span>
                        <span className="text-white/60">Goal: ${project.hardCap}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Time Remaining */}
                {project.status === 'live' && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-brand-primary/10 border border-brand-primary rounded-sm">
                    <Clock className="text-brand-primary" size={16} />
                    <span className="text-brand-primary text-sm font-medium">
                      Ends in: {getTimeRemaining(project.endDate)}
                    </span>
                  </div>
                )}

                {project.status === 'upcoming' && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-blue-500/10 border border-blue-500 rounded-sm">
                    <Clock className="text-blue-500" size={16} />
                    <span className="text-blue-500 text-sm font-medium">
                      Starts: {new Date(project.startDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* Action Button */}
                <Button 
                  className={`w-full ${project.status === 'completed' ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={() => {}}
                  disabled={project.status === 'completed'}
                >
                  {project.status === 'live' && 'Invest Now'}
                  {project.status === 'upcoming' && 'View Details'}
                  {project.status === 'completed' && 'Completed'}
                  {project.status !== 'completed' && <ExternalLink size={16} />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-20">
            <p className="text-white/60 text-lg">No projects found in this category</p>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-20 bg-white/5 border border-white/25 rounded-sm p-8 backdrop-blur-xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <TrendingUp className="text-brand-primary" size={28} />
              </div>
              <h3 className="text-white font-semibold mb-2">Vetted Projects</h3>
              <p className="text-white/60 text-sm">Every project undergoes rigorous due diligence and smart contract audits</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <Users className="text-brand-primary" size={28} />
              </div>
              <h3 className="text-white font-semibold mb-2">Community Driven</h3>
              <p className="text-white/60 text-sm">Join a thriving community of investors and early adopters</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <Clock className="text-brand-primary" size={28} />
              </div>
              <h3 className="text-white font-semibold mb-2">Early Access</h3>
              <p className="text-white/60 text-sm">Get in before public launches with exclusive early-bird pricing</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
